/**
 * 지출 전자증빙 (PoC · localStorage)
 * - 법인카드 사용내역 · 매입세금계산서
 * - 카드번호 → 기본 예산축 매핑
 * - 결의 연결: evidence.resolutionUid / resolutionNo (미결 목록 제외 · 삭제·반납 시 해제)
 *
 * 키: erp.expense.evidence|{tenant}|{fy}
 *     erp.expense.cardAxisMap|{tenant}
 */

import { getCurrentFiscalYear } from 'components/forms';
import {
  isBdApiEnabled,
  POC_BUCKET,
  pocParams,
  loadPocRows,
  savePocRows,
  enqueuePersist,
  cacheGet,
  cacheSet,
} from 'services/pocRowPersist';

export const EVIDENCE_STORAGE_PREFIX = 'erp.expense.evidence';
export const CARD_AXIS_MAP_PREFIX = 'erp.expense.cardAxisMap';

export const EVIDENCE_SOURCE = {
  CARD: 'CARD',
  TAX: 'TAX',
};

export const EVIDENCE_SOURCE_OPTIONS = [
  { value: EVIDENCE_SOURCE.CARD, label: '법인카드' },
  { value: EVIDENCE_SOURCE.TAX, label: '세금계산서' },
];

export function evidenceStorageKey(tenantCode, fiscalYear) {
  return `${EVIDENCE_STORAGE_PREFIX}|${tenantCode || ''}|${fiscalYear || ''}`;
}

export function cardAxisMapKey(tenantCode) {
  return `${CARD_AXIS_MAP_PREFIX}|${tenantCode || ''}`;
}

export function createEvidenceUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `EVD-${crypto.randomUUID()}`;
  }
  return `EVD-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

const evidenceCache = new Map();
const cardAxisCache = new Map();

function keepRows(rows) {
  return (rows || []).filter((r) => r && r.mod !== 'D').map((r) => ({ ...r, mod: '' }));
}

export function emptyEvidence(partial = {}) {
  return {
    evidenceUid: '',
    source: EVIDENCE_SOURCE.CARD,
    fiscalYear: '',
    /** 외부 시스템 키 */
    externalKey: '',
    useDate: '',
    cardNo: '',
    cardHolder: '',
    merchantName: '',
    businessNo: '',
    amount: 0,
    vatAmt: 0,
    vatYn: 'N',
    approveNo: '',
    remark: '',
    /** 결의에 연결된 경우 (점유 → 미결 제외) */
    resolutionUid: '',
    /** 결의번호(표시·감사). 저장 시 채움 */
    resolutionNo: '',
    /** type1 연결 임시점유 — 채번 전 타 결의 사용 불가 */
    draftSessionUid: '',
    mod: '',
    ...partial,
  };
}

/** 카드 → 기본 예산축 */
export function emptyCardAxisMap(partial = {}) {
  return {
    cardNo: '',
    deptCode: '',
    deptName: '',
    projectCode: '',
    projectName: '',
    subjectCode: '',
    subjectName: '',
    fundCode: '',
    fundName: '',
    balanceUid: '',
    ...partial,
  };
}

export function loadEvidences(tenantCode, fiscalYear) {
  const key = evidenceStorageKey(tenantCode, fiscalYear);
  const cached = cacheGet(evidenceCache, key);
  if (cached) return cached;
  if (isBdApiEnabled()) return [];
  const rows = readJson(key, []);
  cacheSet(evidenceCache, key, rows);
  return rows;
}

export async function fetchEvidences(tenantCode, fiscalYear) {
  const key = evidenceStorageKey(tenantCode, fiscalYear);
  if (isBdApiEnabled()) {
    return loadPocRows({
      bucket: POC_BUCKET.EVIDENCE,
      params: pocParams({ tenantCode, fiscalYear }),
      cache: evidenceCache,
      cacheKey: key,
      uidKey: 'evidenceUid',
    });
  }
  return loadEvidences(tenantCode, fiscalYear);
}

export function saveEvidences(tenantCode, fiscalYear, rows) {
  const key = evidenceStorageKey(tenantCode, fiscalYear);
  const kept = keepRows(rows);
  cacheSet(evidenceCache, key, kept);
  if (isBdApiEnabled()) {
    enqueuePersist(key, () =>
      savePocRows({
        bucket: POC_BUCKET.EVIDENCE,
        params: pocParams({ tenantCode, fiscalYear }),
        cache: evidenceCache,
        cacheKey: key,
        uidKey: 'evidenceUid',
        nextRows: kept,
        ensureUid: () => createEvidenceUid(),
      })
    );
    return kept;
  }
  writeJson(key, kept);
  return kept;
}

export function loadCardAxisMaps(tenantCode) {
  const key = cardAxisMapKey(tenantCode);
  const cached = cacheGet(cardAxisCache, key);
  if (cached) return cached;
  if (isBdApiEnabled()) return [];
  const rows = readJson(key, []);
  cacheSet(cardAxisCache, key, rows);
  return rows;
}

export async function fetchCardAxisMaps(tenantCode) {
  const key = cardAxisMapKey(tenantCode);
  if (isBdApiEnabled()) {
    return loadPocRows({
      bucket: POC_BUCKET.CARD_AXIS,
      params: pocParams({ tenantCode }),
      cache: cardAxisCache,
      cacheKey: key,
      uidKey: 'cardNo',
    });
  }
  return loadCardAxisMaps(tenantCode);
}

export function saveCardAxisMaps(tenantCode, rows) {
  const key = cardAxisMapKey(tenantCode);
  const kept = keepRows(rows);
  cacheSet(cardAxisCache, key, kept);
  if (isBdApiEnabled()) {
    enqueuePersist(key, () =>
      savePocRows({
        bucket: POC_BUCKET.CARD_AXIS,
        params: pocParams({ tenantCode }),
        cache: cardAxisCache,
        cacheKey: key,
        uidKey: 'cardNo',
        nextRows: kept,
      })
    );
    return kept;
  }
  writeJson(key, kept);
  return kept;
}

export function normalizeCardNo(v) {
  return String(v || '').replace(/\s+/g, '');
}

/** 미결의 증빙 (resolutionUid·draftSessionUid 없음) */
export function filterOpenEvidences(rows = [], { source } = {}) {
  let list = (rows || []).filter(
    (r) =>
      r.mod !== 'D' &&
      !String(r.resolutionUid || '').trim() &&
      !String(r.draftSessionUid || '').trim()
  );
  if (source) list = list.filter((r) => r.source === source);
  return list;
}

/**
 * 선택 증빙들의 카드번호로 기본축 추론
 * - 카드가 하나이고 매핑 있으면 그 축
 * - 여러 카드면 매핑이 모두 동일할 때만 채움
 * @returns {{ axis: object, complete: boolean, reason: string }}
 */
export function resolveAxisFromEvidences(tenantCode, evidences = []) {
  const maps = loadCardAxisMaps(tenantCode);
  const byCard = new Map(maps.map((m) => [normalizeCardNo(m.cardNo), m]));

  const cards = [
    ...new Set(
      (evidences || []).map((e) => normalizeCardNo(e.cardNo)).filter(Boolean)
    ),
  ];

  if (!cards.length) {
    const onlyTax =
      (evidences || []).length > 0 &&
      (evidences || []).every((e) => e.source === EVIDENCE_SOURCE.TAX);
    return {
      axis: emptyCardAxisMap(),
      complete: false,
      reason: onlyTax ? '세금계산서는 예산축을 지정하세요' : '카드번호 없음',
    };
  }

  const axes = cards.map((c) => byCard.get(c)).filter(Boolean);
  if (axes.length !== cards.length) {
    return {
      axis: axes[0] ? { ...emptyCardAxisMap(), ...axes[0] } : emptyCardAxisMap(),
      complete: false,
      reason: '카드 기본축 미등록',
    };
  }

  const keyOf = (a) =>
    [a.deptCode, a.projectCode, a.subjectCode, a.fundCode].map((x) => String(x || '').trim()).join('|');
  const firstKey = keyOf(axes[0]);
  if (!axes.every((a) => keyOf(a) === firstKey)) {
    return { axis: emptyCardAxisMap(), complete: false, reason: '선택 건의 기본축이 서로 다름' };
  }

  const axis = { ...emptyCardAxisMap(), ...axes[0] };
  const complete = !!(axis.deptCode && axis.projectCode && axis.subjectCode);
  return {
    axis,
    complete,
    reason: complete ? '' : '기본축에 부서·사업·과목 미비',
  };
}

export function isAxisComplete(axis = {}) {
  return !!(
    String(axis.deptCode || '').trim() &&
    String(axis.projectCode || '').trim() &&
    String(axis.subjectCode || '').trim()
  );
}

/** 증빙 → 결의 디테일 행 */
export function detailFromEvidence(ev, resolutionUid) {
  const e = ev || {};
  return {
    detailUid: '',
    resolutionUid: resolutionUid || '',
    vendorCode: '',
    vendorName: e.merchantName || '',
    evidenceType: e.source === EVIDENCE_SOURCE.TAX ? '세금계산서' : '법인카드',
    evidenceNo: e.approveNo || e.externalKey || '',
    evidenceUid: e.evidenceUid || '',
    vatYn: e.vatYn || (Number(e.vatAmt) > 0 ? 'Y' : 'N'),
    vatAmt: Number(e.vatAmt) || 0,
    accountDr: '',
    accountCr: '',
    bankName: '',
    accountNo: '',
    cardNo: e.cardNo || '',
    amount: Number(e.amount) || 0,
    remark: e.remark || e.merchantName || '',
    mod: 'A',
  };
}

/**
 * 결의에 증빙 연결 (초안 점유)
 * @param {string} [resolutionNo] 저장 전엔 비울 수 있음
 */
export function linkEvidencesToResolution(
  tenantCode,
  fiscalYear,
  evidenceUids,
  resolutionUid,
  resolutionNo = ''
) {
  const uidSet = new Set((evidenceUids || []).map(String).filter(Boolean));
  if (!uidSet.size) return loadEvidences(tenantCode, fiscalYear);
  const resUid = String(resolutionUid || '').trim();
  const resNo = String(resolutionNo || '').trim();
  const all = loadEvidences(tenantCode, fiscalYear);
  const next = all.map((r) => {
    if (!uidSet.has(String(r.evidenceUid))) return r;
    return {
      ...r,
      resolutionUid: resUid,
      resolutionNo: resUid ? resNo : '',
      mod: r.mod === 'A' ? 'A' : 'U',
    };
  });
  saveEvidences(tenantCode, fiscalYear, next);
  return next;
}

/** 연결 해제 (초안 폐기 · 디테일 행 제거) */
export function unlinkEvidences(tenantCode, fiscalYear, evidenceUids) {
  return linkEvidencesToResolution(tenantCode, fiscalYear, evidenceUids, '', '');
}

/**
 * 저장 시 디테일 evidenceUid 기준으로 해당 결의 연결 전량 재동기화
 * - 집합 안: resolutionUid/No 기록
 * - 같은 결의인데 집합 밖: 해제(미결 복귀)
 */
export function syncEvidencesForResolution(
  tenantCode,
  fiscalYear,
  { resolutionUid, resolutionNo = '', evidenceUids = [] } = {}
) {
  const resUid = String(resolutionUid || '').trim();
  if (!resUid) return loadEvidences(tenantCode, fiscalYear);
  const keep = new Set((evidenceUids || []).map(String).filter(Boolean));
  const resNo = String(resolutionNo || '').trim();
  const all = loadEvidences(tenantCode, fiscalYear);
  const next = all.map((r) => {
    const evUid = String(r.evidenceUid || '');
    const linkedHere = String(r.resolutionUid || '').trim() === resUid;
    if (keep.has(evUid)) {
      return {
        ...r,
        resolutionUid: resUid,
        resolutionNo: resNo,
        mod: r.mod === 'A' ? 'A' : 'U',
      };
    }
    if (linkedHere) {
      return { ...r, resolutionUid: '', resolutionNo: '', mod: r.mod === 'A' ? 'A' : 'U' };
    }
    return r;
  });
  saveEvidences(tenantCode, fiscalYear, next);
  return next;
}

/** 결의 삭제·반납 시 해당 결의에 묶인 증빙 전부 해제 */
export function releaseEvidencesByResolution(tenantCode, fiscalYear, resolutionUid) {
  const resUid = String(resolutionUid || '').trim();
  if (!resUid) return loadEvidences(tenantCode, fiscalYear);
  const all = loadEvidences(tenantCode, fiscalYear);
  const next = all.map((r) => {
    if (String(r.resolutionUid || '').trim() !== resUid) return r;
    return {
      ...r,
      resolutionUid: '',
      resolutionNo: '',
      draftSessionUid: '',
      mod: r.mod === 'A' ? 'A' : 'U',
    };
  });
  saveEvidences(tenantCode, fiscalYear, next);
  return next;
}

/** type1 연결 — 임시점유 (미결 제외, 결의번호 없음) */
export function occupyEvidencesDraft(tenantCode, fiscalYear, evidenceUids, draftSessionUid) {
  const uidSet = new Set((evidenceUids || []).map(String).filter(Boolean));
  const draftUid = String(draftSessionUid || '').trim();
  if (!uidSet.size || !draftUid) return loadEvidences(tenantCode, fiscalYear);
  const all = loadEvidences(tenantCode, fiscalYear);
  const next = all.map((r) => {
    if (!uidSet.has(String(r.evidenceUid))) return r;
    return {
      ...r,
      draftSessionUid: draftUid,
      resolutionUid: '',
      resolutionNo: '',
      mod: r.mod === 'A' ? 'A' : 'U',
    };
  });
  saveEvidences(tenantCode, fiscalYear, next);
  return next;
}

/** type1 임시세션 해제 — 해당 draft 점유 전부 미결 복귀 */
export function releaseEvidencesDraft(tenantCode, fiscalYear, draftSessionUid) {
  const draftUid = String(draftSessionUid || '').trim();
  if (!draftUid) return loadEvidences(tenantCode, fiscalYear);
  const all = loadEvidences(tenantCode, fiscalYear);
  const next = all.map((r) => {
    if (String(r.draftSessionUid || '').trim() !== draftUid) return r;
    return {
      ...r,
      draftSessionUid: '',
      mod: r.mod === 'A' ? 'A' : 'U',
    };
  });
  saveEvidences(tenantCode, fiscalYear, next);
  return next;
}

/**
 * 임시점유 → 결의 점유 승격 (같은 트랜잭션성 갱신)
 */
export function promoteEvidencesDraftToResolution(
  tenantCode,
  fiscalYear,
  { draftSessionUid, resolutionUid, resolutionNo = '', evidenceUids = [] } = {}
) {
  const draftUid = String(draftSessionUid || '').trim();
  const resUid = String(resolutionUid || '').trim();
  if (!draftUid || !resUid) return loadEvidences(tenantCode, fiscalYear);
  const keep = new Set((evidenceUids || []).map(String).filter(Boolean));
  const resNo = String(resolutionNo || '').trim();
  const all = loadEvidences(tenantCode, fiscalYear);
  const next = all.map((r) => {
    const evUid = String(r.evidenceUid || '');
    if (keep.has(evUid)) {
      return {
        ...r,
        draftSessionUid: '',
        resolutionUid: resUid,
        resolutionNo: resNo,
        mod: r.mod === 'A' ? 'A' : 'U',
      };
    }
    if (String(r.draftSessionUid || '').trim() === draftUid) {
      return {
        ...r,
        draftSessionUid: '',
        mod: r.mod === 'A' ? 'A' : 'U',
      };
    }
    return r;
  });
  saveEvidences(tenantCode, fiscalYear, next);
  return next;
}

/**
 * 고아 연결 정리 — 살아있는 결의 UID 집합에 없는 resolutionUid는 해제
 * @param {string[]} aliveResolutionUids mod≠D 인 결의 UID
 * @param {string[]} [aliveDraftSessionUids] 살아있는 type1 임시세션
 */
export function reconcileOrphanEvidenceLinks(
  tenantCode,
  fiscalYear,
  aliveResolutionUids = [],
  aliveDraftSessionUids = []
) {
  const alive = new Set((aliveResolutionUids || []).map(String).filter(Boolean));
  const aliveDraft = new Set((aliveDraftSessionUids || []).map(String).filter(Boolean));
  const all = loadEvidences(tenantCode, fiscalYear);
  let changed = false;
  const next = all.map((r) => {
    let row = r;
    const linked = String(r.resolutionUid || '').trim();
    if (linked && !alive.has(linked)) {
      changed = true;
      row = { ...row, resolutionUid: '', resolutionNo: '', mod: r.mod === 'A' ? 'A' : 'U' };
    }
    const draft = String(row.draftSessionUid || '').trim();
    if (draft && !aliveDraft.has(draft)) {
      changed = true;
      row = { ...row, draftSessionUid: '', mod: row.mod === 'A' ? 'A' : 'U' };
    }
    return row;
  });
  if (changed) saveEvidences(tenantCode, fiscalYear, next);
  return next;
}

/**
 * PoC 시드 — 법인카드·세금계산서 대량 샘플 (테스트용 각 100건)
 */

const CARD_HOLDERS = ['김담당', '이출장', '박운영', '최회계', '정사업', '한총무'];
const CARD_NOS = [
  '9410-****-****-1234',
  '9410-****-****-5678',
  '5521-****-****-1001',
  '5521-****-****-2002',
];
const CARD_MERCHANTS = [
  '오피스디포 강남',
  '스타벅스 시청',
  'KTX 예매',
  '이마트 용산',
  '쿠팡이츠',
  'GS25 시청점',
  '교보문고 광화문',
  '홈플러스 월드컵',
  '세븐일레븐 을지로',
  'CGV 명동',
  '올리브영 명동',
  '다이소 종로',
  '우아한형제들',
  '카카오 T',
  '대한항공',
  '코레일톡',
  '네이버페이 가맹',
  '롯데호텔 서울',
  '메가박스 코엑스',
  '파리바게뜨 시청',
];
const TAX_MERCHANTS = [
  { name: '(주)오피스월드', biz: '123-81-67890' },
  { name: '한국전산시스템', biz: '214-85-12345' },
  { name: '(주)한빛소프트', biz: '110-81-23456' },
  { name: '미래사무기', biz: '220-87-34567' },
  { name: '(주)그린페이퍼', biz: '312-81-45678' },
  { name: '대한물류서비스', biz: '123-86-56789' },
  { name: '(주)스마트빌딩', biz: '215-88-67890' },
  { name: '서울인쇄산업', biz: '101-81-78901' },
  { name: '(주)클린서비스', biz: '202-85-89012' },
  { name: '넥스트보안', biz: '314-81-90123' },
  { name: '(주)에듀콘텐츠', biz: '120-86-01234' },
  { name: '코리아렌탈', biz: '211-87-11223' },
];
const CARD_REMARKS = ['사무용품', '회의 다과', '출장 교통', '야근 식대', '소모품', '도서구입', '택배비', '주차비'];
const TAX_REMARKS = ['복사용지·토너', '유지보수비', 'SW라이선스', '인쇄비', '청소용역', '보안장비', '교육교재', '렌탈료'];

function pad(n, w = 3) {
  return String(n).padStart(w, '0');
}

function ymdFromIndex(fy, index, spanDays = 120) {
  const start = new Date(Number(fy), 0, 5);
  const d = new Date(start.getTime() + (index % spanDays) * 86400000);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function moneyFromIndex(base, index, step = 1370) {
  return base + (index % 40) * step + (index % 7) * 100;
}

/** 법인카드 사용내역 N건 */
export function buildCardSeedRows(fy, count = 100) {
  const n = Math.max(0, Number(count) || 0);
  const rows = [];
  for (let i = 1; i <= n; i += 1) {
    const useDate = ymdFromIndex(fy, i - 1);
    const amount = moneyFromIndex(15000, i, 2500);
    const withVat = i % 5 !== 0;
    const vatAmt = withVat ? Math.round(amount / 11) : 0;
    rows.push(
      emptyEvidence({
        evidenceUid: createEvidenceUid(),
        source: EVIDENCE_SOURCE.CARD,
        fiscalYear: fy,
        externalKey: `CARD-${fy}${pad(i, 4)}`,
        useDate,
        cardNo: CARD_NOS[(i - 1) % CARD_NOS.length],
        cardHolder: CARD_HOLDERS[(i - 1) % CARD_HOLDERS.length],
        merchantName: CARD_MERCHANTS[(i - 1) % CARD_MERCHANTS.length],
        businessNo: i % 4 === 0 ? '' : `12081${pad(i % 1000, 5)}`,
        amount,
        vatAmt,
        vatYn: withVat ? 'Y' : 'N',
        approveNo: `A${fy.slice(2)}${pad(i, 5)}`,
        remark: CARD_REMARKS[(i - 1) % CARD_REMARKS.length],
        mod: 'A',
      })
    );
  }
  return rows;
}

/** 매입세금계산서 N건 */
export function buildTaxSeedRows(fy, count = 100) {
  const n = Math.max(0, Number(count) || 0);
  const rows = [];
  for (let i = 1; i <= n; i += 1) {
    const useDate = ymdFromIndex(fy, i - 1, 150);
    const supply = moneyFromIndex(100000, i, 5500);
    const vatAmt = Math.round(supply * 0.1);
    const amount = supply + vatAmt;
    const m = TAX_MERCHANTS[(i - 1) % TAX_MERCHANTS.length];
    const mmdd = useDate.replace(/-/g, '').slice(4);
    rows.push(
      emptyEvidence({
        evidenceUid: createEvidenceUid(),
        source: EVIDENCE_SOURCE.TAX,
        fiscalYear: fy,
        externalKey: `TAX-${fy}${pad(i, 4)}`,
        useDate,
        merchantName: m.name,
        businessNo: m.biz,
        amount,
        vatAmt,
        vatYn: 'Y',
        approveNo: `${mmdd}-${pad(i, 8)}-${pad(1000000000 + i, 10)}`,
        remark: TAX_REMARKS[(i - 1) % TAX_REMARKS.length],
        mod: 'A',
      })
    );
  }
  return rows;
}

const SAMPLE_CARD_TARGET = 100;
const SAMPLE_TAX_TARGET = 100;

function countBySource(rows, source) {
  return (rows || []).filter((e) => e.source === source && e.mod !== 'D').length;
}

function ensureDefaultCardAxisMaps(tenantCode) {
  const existing = loadCardAxisMaps(tenantCode) || [];
  if (existing.length) return existing;
  const maps = CARD_NOS.slice(0, 2).map((cardNo, idx) =>
    emptyCardAxisMap({
      cardNo,
      deptCode: idx === 0 ? 'A01' : 'A02',
      deptName: idx === 0 ? 'a팀' : 'b팀',
      projectCode: idx === 0 ? 'P100' : 'P200',
      projectName: idx === 0 ? '기관운영' : '사업운영',
      subjectCode: '51101',
      subjectName: '일반운영비',
      fundCode: '01',
      fundName: '일반회계',
    })
  );
  saveCardAxisMaps(tenantCode, maps);
  return maps;
}

/**
 * 전자증빙 시드 보장 — 법인카드·세금계산서 각 100건(미만이면 보충)
 */
export function ensureEvidenceSeed(tenantCode, fiscalYear) {
  const fy = String(fiscalYear || getCurrentFiscalYear()).trim();
  const maps = ensureDefaultCardAxisMaps(tenantCode);
  let evidences = loadEvidences(tenantCode, fy) || [];

  const cardNeed = Math.max(0, SAMPLE_CARD_TARGET - countBySource(evidences, EVIDENCE_SOURCE.CARD));
  const taxNeed = Math.max(0, SAMPLE_TAX_TARGET - countBySource(evidences, EVIDENCE_SOURCE.TAX));

  if (cardNeed > 0 || taxNeed > 0) {
    const add = [
      ...(cardNeed > 0 ? buildCardSeedRows(fy, cardNeed) : []),
      ...(taxNeed > 0 ? buildTaxSeedRows(fy, taxNeed) : []),
    ];
    evidences = [...evidences, ...add];
    saveEvidences(tenantCode, fy, evidences);
  }

  return { evidences, maps };
}

/**
 * 테스트용 — 미결 샘플을 목표 건수로 재시드(기존 결의연결 건은 유지)
 * @param {{ cardCount?: number, taxCount?: number }} [opts]
 */
export function seedEvidenceSamples(tenantCode, fiscalYear, opts = {}) {
  const fy = String(fiscalYear || getCurrentFiscalYear()).trim();
  const cardTarget = Number(opts.cardCount ?? SAMPLE_CARD_TARGET) || 0;
  const taxTarget = Number(opts.taxCount ?? SAMPLE_TAX_TARGET) || 0;
  ensureDefaultCardAxisMaps(tenantCode);

  const all = loadEvidences(tenantCode, fy) || [];
  // 결의에 묶인 건·삭제 건은 유지, 미결 CARD/TAX만 교체
  const keep = all.filter(
    (e) =>
      e.mod === 'D' ||
      String(e.resolutionUid || '').trim() ||
      (e.source !== EVIDENCE_SOURCE.CARD && e.source !== EVIDENCE_SOURCE.TAX)
  );
  const next = [
    ...keep,
    ...buildCardSeedRows(fy, cardTarget),
    ...buildTaxSeedRows(fy, taxTarget),
  ];
  saveEvidences(tenantCode, fy, next);
  return next;
}

export function evidenceSourceLabel(code) {
  return EVIDENCE_SOURCE_OPTIONS.find((o) => o.value === code)?.label || code || '';
}
